import { useState } from 'react';
import {
  Dialog,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { BiX } from 'react-icons/bi';
import IntlMessages from 'util/IntlMessages';

import HeartBeatDayChart from '../HeartbeatDayChart';
import HeartbeatDonutChart from '../HeartbeatDonutChart';
import HeartbeatDonutLabels from '../HeartbeatDonutLabels';
import HeartbeatTotalUptime from '../HeartbeatTotalUptime';
import { type PeriodType, type UptimePeriod } from '../interfaces';
import {
  timeSpent,
  timezone,
  uptimeDataMock,
  uptimePercentage,
  uptimeSummary,
} from './uptimeDataMock';

type UptimeOnboardingProps = {
  open: boolean;
  onClose: () => void;
};

const uptimePeriodOptions: UptimePeriod[] = [
  { value: 'lastDay', label: 'Últimas 24 hrs' },
  { value: 'lastWeek', label: 'Últimos 7 días' },
  { value: 'lastMonth', label: 'Últimos 30 días' },
];

const UptimeOnboarding: React.FC<UptimeOnboardingProps> = ({ open, onClose }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<UptimePeriod>(uptimePeriodOptions[0]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value as PeriodType;
    const newPeriod = uptimePeriodOptions.find((period) => period.value === selectedValue);
    if (newPeriod) return setSelectedPeriod(newPeriod);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth={'xl'}
      className="chargepoint-uptime-modal dialog"
    >
      <div className="chargepoint-uptime-modal dialog-container" style={{ width: '1024px' }}>
        <section className="dialog-title uptime-modal-title">
          <div className="dialog-title-content">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Typography className="dialog-title-typography">
                  <IntlMessages id="components.charge-point-section.uptime-modal.title" />
                </Typography>

                <Typography
                  fontSize={16}
                  color={'#BFBFBF'}
                  style={{
                    paddingTop: '5px',
                  }}
                >
                  Cargador 1
                </Typography>
              </div>
              <Typography fontSize={14} color={'#BFBFBF'}>
                Americo Vespucio 100, Las Condes, RM
              </Typography>
            </div>

            <BiX size={28} onClick={onClose} className="dialog-title-icon" />
          </div>
          <Divider className="dialog-title-divider" />
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
          <section
            className="uptime-modal-daily-chart"
            style={{ display: 'flex', flexDirection: 'column', gap: 15 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Typography fontSize={16} fontWeight={700}>
                  <IntlMessages id="components.charge-point-section.uptime-modal.lastDay-uptime-title" />
                </Typography>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div
                    style={{
                      height: 10,
                      width: 10,
                      borderRadius: '50%',
                      backgroundColor: '#2AFD95',
                    }}
                  ></div>
                  <Typography fontSize={14}>
                    <IntlMessages id="components.charge-point-section.uptime-modal.lastDay-uptime-online" />
                  </Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div
                    style={{
                      height: 10,
                      width: 10,
                      borderRadius: '50%',
                      backgroundColor: '#E6492D',
                    }}
                  ></div>
                  <Typography fontSize={14}>
                    <IntlMessages id="components.charge-point-section.uptime-modal.lastDay-uptime-offline" />
                  </Typography>
                </div>
              </div>
            </div>
            <HeartBeatDayChart heartbeats={uptimeDataMock} timezone={timezone} />
          </section>

          <section
            className="uptime-modal-summary-chart"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              padding: 40,
              borderRadius: 4,
              gap: 70,
            }}
          >
            <div
              className="uptime-modal-period-select"
              style={{ display: 'flex', alignItems: 'center', gap: 40 }}
            >
              <Typography fontSize={20} fontWeight={700}>
                <IntlMessages id="components.charge-point-section.uptime-modal.chargepoint-detail" />
              </Typography>

              <FormControl fullWidth sx={{ minWidth: '200px', maxWidth: '200px' }}>
                <InputLabel shrink id="period-label">
                  Selecciona un periodo
                </InputLabel>
                <Select
                  labelId="period-label"
                  label="Selecciona un periodo"
                  id="period-select"
                  fullWidth
                  size="small"
                  notched
                  value={selectedPeriod.value}
                  onChange={handlePeriodChange}
                >
                  {uptimePeriodOptions.map((period) => (
                    <MenuItem key={period.value} value={period.value}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 75 }}>
              <HeartbeatTotalUptime uptime={uptimePercentage} isLoading={false} />

              <Divider orientation="vertical" flexItem />

              <div
                className="uptime-modal-donut-chart"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <HeartbeatDonutChart id={1} uptimeSummary={uptimeSummary} />

                <HeartbeatDonutLabels
                  timeSpent={timeSpent}
                  periodType={selectedPeriod.value}
                  isLoading={false}
                />
              </div>
            </div>
          </section>
        </section>
      </div>
    </Dialog>
  );
};

export default UptimeOnboarding;
